import { useMemo, useRef, useState } from "react";

import useUploadESignatureTemplate from "../hooks/useUploadESignatureTemplate";

const STEPS = ["Case Type", "Document"];

const getRequestError = (error) => {
  const detail = error?.response?.data?.detail;
  const message = error?.response?.data?.message;

  if (typeof detail === "string") return detail;
  if (typeof message === "string") return message;
  return "Unable to upload the E-Signature template. Please try again.";
};

const UploadESignatureTemplate = ({
  caseTypesByCategory,
  caseTypesLoading,
  caseTypesError,
  onRetryCaseTypes,
  onUploaded,
}) => {
  const [step, setStep] = useState(0);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);
  const [selectedCaseTypeIds, setSelectedCaseTypeIds] = useState([]);
  const [caseTypeQuery, setCaseTypeQuery] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [validationError, setValidationError] = useState("");
  const fileInputRef = useRef(null);
  const {
    uploadTemplate,
    loading: uploading,
    error: uploadError,
    clearError,
  } = useUploadESignatureTemplate();

  const caseTypes = useMemo(() => Object.values(caseTypesByCategory).flat(), [caseTypesByCategory]);

  const filteredCategories = useMemo(() => {
    const query = caseTypeQuery.trim().toLowerCase();

    return Object.entries(caseTypesByCategory)
      .map(([category, items]) => [
        category,
        (items || []).filter((caseType) => caseType.name?.toLowerCase().includes(query)),
      ])
      .filter(([, items]) => items.length);
  }, [caseTypeQuery, caseTypesByCategory]);

  const currentBlocker =
    step === 0
      ? caseTypesLoading
        ? "Loading case types…"
        : caseTypesError
          ? "Case types could not be loaded."
          : selectedCaseTypeIds.length === 0
            ? "Select at least one case type to continue."
            : ""
      : !uploadedFile
        ? "Upload a PDF document to continue."
        : "";

  const displayedError = validationError || (uploadError ? getRequestError(uploadError) : "");

  const clearErrors = () => {
    setValidationError("");
    clearError();
  };

  const acceptFile = (file) => {
    if (!file || uploading) return;

    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    if (!isPdf || file.size === 0) {
      setValidationError("Choose a non-empty PDF document.");
      setUploadedFile(null);
      return;
    }

    setUploadedFile(file);
    clearErrors();
  };

  const handleUpload = async () => {
    if (selectedCaseTypeIds.length === 0 || !uploadedFile || uploading) return;

    clearErrors();
    try {
      await uploadTemplate({ caseTypeIds: selectedCaseTypeIds, file: uploadedFile });
      onUploaded();
    } catch {
      // The upload hook exposes the request error next to the controls.
    }
  };

  const handleNext = () => {
    if (currentBlocker) return;
    setMaxUnlockedStep(1);
    setStep(1);
  };

  const controls = (
    <div className="template-wizard-actions">
      <button
        type="button"
        className="btn btn-outline-secondary height-25 template-wizard-button"
        disabled={step === 0 || uploading}
        onClick={() => setStep(0)}
      >
        ← Back
      </button>
      {step === 0 ? (
        <button
          type="button"
          className={`btn height-25 template-wizard-button template-wizard-primary-button ${
            currentBlocker ? "btn-secondary" : "btn-success"
          }`}
          disabled={Boolean(currentBlocker) || uploading}
          onClick={handleNext}
        >
          Continue to Document <span className="template-next-arrow">→</span>
        </button>
      ) : (
        <button
          type="button"
          className={`btn height-25 template-wizard-button template-wizard-primary-button ${
            currentBlocker || uploading ? "btn-secondary" : "btn-success"
          }`}
          disabled={Boolean(currentBlocker) || uploading}
          onClick={handleUpload}
        >
          {uploading ? "Uploading…" : "Upload Template"}
        </button>
      )}
    </div>
  );

  return (
    <div className="upload-letter-template-wizard esign-template-form">
      <div className="template-wizard-header">
        <ol className="template-wizard-rail">
          {STEPS.map((label, index) => {
            const active = step === index;
            const unlocked = index <= maxUnlockedStep;
            const completed = index === 0 && maxUnlockedStep === 1;
            const status = active
              ? "active"
              : completed
                ? "completed"
                : unlocked
                  ? "unlocked"
                  : "locked";

            return (
              <li
                key={label}
                className={`template-wizard-step ${
                  index === 0 ? "template-wizard-step-first" : "template-wizard-step-last"
                }`}
              >
                <button
                  type="button"
                  disabled={!unlocked || uploading}
                  className={`template-wizard-step-button template-wizard-step-${status}`}
                  onClick={() => setStep(index)}
                  aria-current={active ? "step" : undefined}
                >
                  <span className="template-wizard-step-content">
                    <span className="template-wizard-step-number">
                      {completed ? "✓" : `${index + 1}:`}
                    </span>
                    <span className="template-wizard-label">{label}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div
        style={{
          minHeight: "25px",
        }}
        className="template-wizard-footer height-25 pb-0 pt-1 px-0 template-wizard-controls"
      >
        <div
          className={`template-wizard-message p-0 ${
            currentBlocker || displayedError
              ? "template-wizard-message-required"
              : "template-wizard-message-complete"
          }`}
          role={displayedError ? "alert" : "status"}
        >
          <span className="template-wizard-message-icon">
            {currentBlocker || displayedError ? "!" : "✓"}
          </span>
          <span>
            {displayedError || currentBlocker || "This step is complete. You can continue."}
          </span>
        </div>
        {controls}
      </div>

      <div className="template-wizard-body invisible-scrollbar upload-template-tab-firmsetting">
        {step === 0 && (
          <div className="template-step-panel p-0">
            <div className="template-step-toolbar pe-1">
              <input
                type="search"
                className="form-control height-25 p-l-5 template-search-input"
                value={caseTypeQuery}
                placeholder="Filter case types"
                aria-label="Filter case types"
                onChange={(event) => setCaseTypeQuery(event.target.value)}
              />
            </div>

            {caseTypesLoading ? (
              <div className="template-empty-state">Loading case types…</div>
            ) : caseTypesError ? (
              <div className="template-empty-state flex-column gap-2">
                <span>Unable to load case types.</span>
                <button type="button" className="btn btn-link p-0" onClick={onRetryCaseTypes}>
                  Retry
                </button>
              </div>
            ) : filteredCategories.length ? (
              filteredCategories.map(([category, items]) => (
                <div key={category} className="template-choice-group">
                  <div className="template-choice-group-header">
                    <div className="template-choice-group-title">
                      <strong>{category}</strong>
                    </div>
                  </div>
                  <div className="template-option-grid">
                    {items.map((caseType) => {
                      const caseTypeKey = String(caseType.id);
                      const checked = selectedCaseTypeIds.some(
                        (selectedId) => String(selectedId) === caseTypeKey
                      );

                      return (
                        <label
                          key={caseType.id}
                          className={`template-checkbox-option ${
                            checked ? "template-checkbox-option-selected" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            name="esign-case-types"
                            className="form-check mt-0 mb-0 cursor-pointer"
                            checked={checked}
                            onChange={() => {
                              setSelectedCaseTypeIds((currentIds) =>
                                checked
                                  ? currentIds.filter((id) => String(id) !== caseTypeKey)
                                  : [...currentIds, caseType.id]
                              );
                              clearErrors();
                            }}
                          />
                          {caseType.casetype_icon && (
                            <span className="template-case-icon">
                              <img src={caseType.casetype_icon} alt="" />
                            </span>
                          )}
                          <span className="template-checkbox-label">{caseType.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="template-empty-state">
                {caseTypes.length
                  ? `No case types match “${caseTypeQuery}”.`
                  : "No case types are available."}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="template-step-panel p-0">
            <div className="template-document-layout">
              <div className="template-document-main">
                <div
                  role="button"
                  tabIndex={uploading ? -1 : 0}
                  aria-label="Upload E-Signature template PDF"
                  aria-disabled={uploading}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (!uploading && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (!uploading) setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragging(false);
                    acceptFile(event.dataTransfer.files?.[0]);
                  }}
                  className={`template-dropzone ${dragging ? "template-dropzone-dragging" : ""} ${
                    uploadedFile ? "template-dropzone-has-file" : ""
                  }`}
                >
                  {uploadedFile ? (
                    <>
                      <div className="template-file-success-icon">✓</div>
                      <strong>{uploadedFile.name}</strong>
                      <span>{(uploadedFile.size / 1024).toFixed(0)} KB</span>
                      <small>Click or drop another PDF to replace it.</small>
                    </>
                  ) : (
                    <>
                      <div className="template-upload-icon">↑</div>
                      <strong>Drop your E-Signature template here</strong>
                      <span>or click to browse</span>
                      <small>PDF files only</small>
                    </>
                  )}
                  <input
                    type="file"
                    id="esign_template_file"
                    name="file"
                    accept=".pdf,application/pdf"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={(event) => {
                      acceptFile(event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </div>
                {displayedError && <div className="template-upload-error">{displayedError}</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="template-wizard-footer template-wizard-controls">
        <div className="template-wizard-message" />
        {controls}
      </div>
    </div>
  );
};

export default UploadESignatureTemplate;
